import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { X, Copy, CheckCircle } from 'lucide-react';

const METHOD_CONFIG = {
  JazzCash:  { color: '#E91E8C', label: 'JazzCash',  icon: '💜', hint: 'Send to this JazzCash number' },
  Easypaisa: { color: '#00A651', label: 'Easypaisa', icon: '💚', hint: 'Send to this Easypaisa number' },
  Raast:     { color: '#1F6FEB', label: 'Raast',     icon: '🔵', hint: 'Transfer via Raast IBAN' },
  Bank:      { color: '#1a1a1a', label: 'Bank',      icon: '🏦', hint: 'Bank transfer details below' },
};

export default function PaymentQRModal({ method, amount, storeSettings, onRefConfirm, onClose }) {
  const canvasRef = useRef(null);
  const [copied, setCopied]     = useState(false);
  const [refNum, setRefNum]     = useState('');
  const [qrError, setQrError]   = useState(false);

  const cfg = METHOD_CONFIG[method] || METHOD_CONFIG.Bank;

  const accountId = (() => {
    if (method === 'JazzCash')  return storeSettings?.jazzcash_number  || '';
    if (method === 'Easypaisa') return storeSettings?.easypaisa_number || '';
    if (method === 'Raast')     return storeSettings?.raast_iban        || '';
    if (method === 'Bank')      return storeSettings?.bank_iban         || '';
    return '';
  })();

  const bankTitle = storeSettings?.bank_title || storeSettings?.storeName || '';
  const bankName  = storeSettings?.bank_name  || '';

  // QR content: for Raast use deeplink scheme; others encode the account number
  const qrContent = (() => {
    if (!accountId) return '';
    if (method === 'Raast') return `raast://pay?receiverId=${encodeURIComponent(accountId)}&amount=${amount}`;
    return accountId; // JazzCash & Easypaisa: just the mobile number
  })();

  useEffect(() => {
    if (!qrContent || !canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, qrContent, {
      width: 200,
      margin: 2,
      color: { dark: cfg.color, light: '#ffffff' },
      errorCorrectionLevel: 'M',
    }).catch(() => setQrError(true));
  }, [qrContent, cfg.color]);

  const handleCopy = () => {
    navigator.clipboard?.writeText(accountId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleConfirm = () => {
    onRefConfirm(method, refNum.trim());
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-[#1a1a1a]/70 backdrop-blur-sm z-[350] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-xs rounded-2xl shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3" style={{ backgroundColor: cfg.color }}>
          <span className="text-white font-bold text-sm flex items-center gap-2">
            {cfg.icon} {cfg.label} Payment
          </span>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X size={18}/></button>
        </div>

        <div className="p-4 space-y-3">
          {/* Amount */}
          <div className="text-center">
            <p className="text-[10px] text-[#5c5f62] uppercase font-semibold">Amount to Pay</p>
            <p className="text-2xl font-black" style={{ color: cfg.color }}>Rs {amount.toLocaleString('en-PK')}</p>
          </div>

          {/* QR Code */}
          {accountId ? (
            <>
              {!qrError ? (
                <div className="flex justify-center">
                  <div className="p-2 border-2 rounded-xl" style={{ borderColor: cfg.color + '40' }}>
                    <canvas ref={canvasRef} />
                  </div>
                </div>
              ) : (
                <div className="text-center text-xs text-[#5c5f62]">QR unavailable — use number below</div>
              )}

              {/* Account number / IBAN */}
              <div className="bg-[#f6f7f8] rounded-xl p-3 text-center">
                <p className="text-[10px] text-[#5c5f62] uppercase font-semibold mb-1">{cfg.hint}</p>
                {bankTitle && <p className="text-[11px] font-bold text-[#1a1a1a] mb-0.5">{bankTitle}</p>}
                {bankName  && <p className="text-[10px] text-[#5c5f62] mb-0.5">{bankName}</p>}
                <div className="flex items-center justify-center gap-2">
                  <span className="font-mono font-bold text-sm" style={{ color: cfg.color }}>{accountId}</span>
                  <button onClick={handleCopy} className="text-[#5c5f62] hover:text-[#1a1a1a]">
                    {copied ? <CheckCircle size={14} className="text-[#008060]"/> : <Copy size={14}/>}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-[#fff3cd] border border-[#ffea8a] rounded-xl p-3 text-center text-xs text-[#8a6116]">
              ⚠️ No {cfg.label} account configured.<br/>
              Add it in Settings → Payments.
            </div>
          )}

          {/* Reference number input */}
          <div>
            <label className="text-[10px] font-bold text-[#5c5f62] uppercase block mb-1">
              Transaction / Reference No. <span className="text-[#5c5f62] font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={refNum}
              onChange={e => setRefNum(e.target.value)}
              placeholder="Enter TxID after customer pays"
              className="w-full p-2 border border-[#c9cccf] rounded-lg text-xs font-mono focus:outline-none focus:border-[#008060]"
            />
          </div>

          <button
            onClick={handleConfirm}
            className="w-full text-white font-bold py-2.5 rounded-xl text-sm transition-colors"
            style={{ backgroundColor: cfg.color }}
          >
            Confirm Payment Received
          </button>
        </div>
      </div>
    </div>
  );
}
